import React, { useState, useRef, useEffect } from "react";
import uploadImageIcon from "../../../assets/icons/upload_image.png";
import { listFoldersFromS3, generatePresignedUrl, listFileNamesFromS3 } from "../../../config/aws";
import { USER_NAME } from "../../../constant";


const fetchAllRefImages = async (setUploadedImages) => {
  try {
    const allrefImages = await listFileNamesFromS3(`USERS/${USER_NAME}/all_refImages/`);

    const allPreSignedrefImageURLs = await Promise.all(allrefImages.map(async (refImage) => {
      const preSignedURL = await generatePresignedUrl(`${refImage}`);
      return preSignedURL;
    }));

    setUploadedImages(allPreSignedrefImageURLs);
  } catch (error) {
    console.error("Error fetching style folders:", error);
  }
};


const fetchAllDefaultCategories = async (setCategories, setItems) => {
  try {
    // Get all category folder names
    const categories = await listFoldersFromS3(`USERS/default_user/all_refImages/`);
    // // console.log('Fetched categories:', categories);
    setCategories(["All", ...categories]);
    let allImages = [];

    for (const category of categories) {
      // List all image file names in this category
      const imageKeys = await listFileNamesFromS3(`USERS/default_user/all_refImages/${category}/`);
      // // console.log(`Fetched ${imageKeys.length} images for category "${category}":`, imageKeys.slice(0, 3)); // Show first 3 keys

      // Debug: Check if any keys end with '/'
      const folderKeys = imageKeys.filter(key => key.endsWith('/'));
      const fileKeys = imageKeys.filter(key => !key.endsWith('/'));
      // // console.log(`Category "${category}": ${folderKeys.length} folders, ${fileKeys.length} files`);

      for (const imageKey of imageKeys) {
        // Only include actual image files (skip folders)
        if (imageKey.endsWith('/')) {
          // // console.log(`Skipping folder: ${imageKey}`);
          continue;
        }
        const preSignedURL = await generatePresignedUrl(imageKey);

        allImages.push({
          src: preSignedURL,
          label: category,
          category,
        });
      }
    }
    // // console.log('Total images loaded:', allImages.length);
    // // console.log('Sample items:', allImages.slice(0, 3));
    // // console.log('Categories in items:', [...new Set(allImages.map(item => item.category))]);
    setItems(allImages);
  } catch (error) {
    console.error("Error fetching style folders:", error);
  }
};


function PromptImageSelector({ onSelectReferenceImage, onClose, selectedTab, setSelectedTab }) {

  const [uploadedImages, setUploadedImages] = useState([]); // array of URLs
  const [selectedImage, setSelectedImage] = useState(null); // {src, label}
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [imageDimensions, setImageDimensions] = useState({}); // Track image dimensions
  const scrollContainerRef = useRef(null);

  const [filteredItems, setFilteredItems] = useState([]);

  // Function to get image dimensions
  const getImageDimensions = (src) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        resolve({ width: 100, height: 100 }); // fallback dimensions
      };
      img.src = src;
    });
  };

  // Load image dimensions when items change
  useEffect(() => {
    const loadDimensions = async () => {
      const dimensions = {};
      for (const item of items) {
        dimensions[item.src] = await getImageDimensions(item.src);
      }
      setImageDimensions(dimensions);
    };

    if (items.length > 0) {
      loadDimensions();
    }
  }, [items]);

  useEffect(() => {
    // Only filter if we have items and categories are loaded
    if (items.length > 0 && categories.length > 0) {
      const filtered = selectedCategory === "All" ? items : items.filter((item) => item.category === selectedCategory);
      // console.log('Filtering debug:', { selectedCategory, itemsCount: items.length, filteredCount: filtered.length, categories: categories, items: items.slice(0, 3), filtered: filtered.slice(0, 3) });
      setFilteredItems(filtered);
    }
  }, [selectedCategory, items, categories]);

  // Ensure selectedCategory is valid when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && selectedCategory !== "All" && !categories.includes(selectedCategory)) {
      // console.log('Resetting selectedCategory from', selectedCategory, 'to "All" because it\'s not in categories:', categories);
      setSelectedCategory("All");
    }
  }, [categories, selectedCategory]);


  useEffect(() => {
    fetchAllRefImages(setUploadedImages);
    fetchAllDefaultCategories(setCategories, setItems);
  }, [])


  const handleImageUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      const url = URL.createObjectURL(e.target.files[0]);
      setUploadedImages((prev) => [...prev, url]);
      setSelectedImage({ src: url, label: null });
    }
  };

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  React.useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, []);

  return (
    <div style={{ width: 520, height: "80vh", margin: "40px auto", padding: 24, borderRadius: 18, background: "#fff", boxShadow: "0 2px 16px #0001", display: "flex", flexDirection: "column", position: "relative" }}>
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center group hover:bg-[#4B3BFF] transition-all duration-300 transform hover:scale-110 hover:rotate-90 z-20"
      >
        <svg
          className="w-4 h-4 transition-colors duration-300"
          style={{ color: '#6B7280' }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ marginBottom: 16 }}>
          <div className="flex justify-between items-center border-b border-gray-200 pb-2 mb-4">
            <div className="flex w-full justify-between">
              <button className={`py-2 px-2 font-bold border-b-2 transition-colors flex-1 text-purple-600 border-purple-600`}>
                Upload Your Design
              </button>
            </div>
          </div>




          <label style={{ display: "block", border: "2px dashed #a259ff", borderRadius: 12, textAlign: "center", cursor: "pointer", marginBottom: 8, background: "#faf7ff", position: "relative", overflow: "hidden", padding: selectedImage ? "0" : "24px" }}>
            {selectedImage ? (
              <div style={{ position: "relative", margin: 0, padding: 0 }}>
                <img
                  src={selectedImage.src}
                  alt="Selected Reference"
                  style={{
                    width: "100%",
                    maxHeight: 200,
                    objectFit: "contain",
                    borderRadius: 8,
                    margin: 0,
                    padding: 0
                  }}
                />
                {/* Centered minus icon overlay */}
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    background: "rgba(255,255,255,0.7)",
                    borderRadius: "50%",
                    width: 44,
                    height: 44,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    zIndex: 2,
                  }}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedImage(null);
                  }}
                >
                  {/* SVG minus icon */}
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="14" cy="14" r="14" fill="#FF3B30" />
                    <rect x="7" y="13" width="14" height="2" rx="1" fill="white" />
                  </svg>
                </div>
              </div>
            ) : (
              <img src={uploadImageIcon} alt="Upload" style={{ width: 32, height: 32, display: "block", margin: "auto" }} />
            )}
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageUpload} />
          </label>
        </div>
        <div style={{ display: "flex", alignItems: "center", margin: "4px 0" }}>
          <div style={{ flex: 1, height: 1, background: "#eee" }} />
          <span style={{ margin: "0 12px", color: "#888" }}>or</span>
          <div style={{ flex: 1, height: 1, background: "#eee" }} />
        </div>
        <div style={{ fontWeight: 500, marginBottom: 12 }}>Select from Collection</div>
        <div
          style={{
            display: "flex",
            marginBottom: 18,
            gap: 8
          }}
        >
          <button
            onClick={() => setSelectedTab("My Uploads")}
            style={{
              flex: 1,
              padding: "18px 0 14px 0",
              border: "none",
              borderRadius: 0,
              background: selectedTab === "My Uploads" ? "#f6f0ff" : "#f6f6f6",
              color: selectedTab === "My Uploads" ? "#6c3cff" : "#888",
              fontWeight: selectedTab === "My Uploads" ? 600 : 500,
              fontSize: 16,
              cursor: "pointer",
              outline: "none",
              borderBottom: selectedTab === "My Uploads" ? "4px solid #6c3cff" : "4px solid transparent",
              transition: "background 0.2s, color 0.2s"
            }}
          >
            My Uploads
          </button>
          <button
            onClick={() => {
              setSelectedTab("Our Collection");
              setSelectedCategory("All");
            }}
            style={{
              flex: 1,
              padding: "18px 0 14px 0",
              border: "none",
              borderRadius: 0,
              background: selectedTab === "Our Collection" ? "#f6f0ff" : "#f6f6f6",
              color: selectedTab === "Our Collection" ? "#6c3cff" : "#888",
              fontWeight: selectedTab === "Our Collection" ? 600 : 500,
              fontSize: 16,
              cursor: "pointer",
              outline: "none",
              borderBottom: selectedTab === "Our Collection" ? "4px solid #6c3cff" : "4px solid transparent",
              transition: "background 0.2s, color 0.2s"
            }}
          >
            Our Collection
          </button>
        </div>
        {selectedTab === "Our Collection" && (
          <div style={{ position: "relative", marginBottom: 18 }}>
            {canScrollLeft && (
              <button
                onClick={scrollLeft}
                style={{
                  position: "absolute",
                  left: -8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 1,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <span style={{ fontSize: 16, color: "#666" }}>‹</span>
              </button>
            )}
            <div
              ref={scrollContainerRef}
              style={{ display: "flex", gap: 8, overflowX: "auto", flexWrap: "nowrap", paddingBottom: 4, scrollbarWidth: "none", msOverflowStyle: "none" }}
              onScroll={checkScrollButtons}
            >
              <style>{`
                div::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    // console.log('Category clicked:', cat);
                    setSelectedCategory(cat);
                  }}
                  style={{
                    padding: "6px 18px",
                    borderRadius: 8,
                    border: selectedCategory === cat ? "2px solid #a259ff" : "1px solid #ccc",
                    background: selectedCategory === cat ? "#f6f0ff" : "#fff",
                    color: selectedCategory === cat ? "#a259ff" : "#333",
                    fontWeight: 500,
                    cursor: "pointer",
                    outline: "none",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>
            {canScrollRight && (
              <button
                onClick={scrollRight}
                style={{
                  position: "absolute",
                  right: -8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 1,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <span style={{ fontSize: 16, color: "#666" }}>›</span>
              </button>
            )}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, minHeight: 120 }}>
          {selectedTab === "My Uploads" && uploadedImages.length === 0 && (
            <div style={{ gridColumn: "1 / -1", color: "#aaa", textAlign: "center", padding: 24, fontSize: 16 }}>
              No uploads yet.
            </div>
          )}
          {selectedTab === "My Uploads" && uploadedImages.map((src, idx) => (
            <div
              key={src}
              onClick={() => setSelectedImage(selectedImage && selectedImage.src === src ? null : { src, label: null })}
              style={{
                border: selectedImage && selectedImage.src === src ? "3px solid #a259ff" : "1px solid #ccc",
                borderRadius: 12,
                padding: 6,
                background: selectedImage && selectedImage.src === src ? "#f6f0ff" : "#fafafa",
                cursor: "pointer",
                position: "relative",
                textAlign: "center",
                boxShadow: selectedImage && selectedImage.src === src ? "0 2px 8px #a259ff22" : "none"
              }}
            >
              <img src={src} alt={`Upload ${idx + 1}`} style={{ width: "100%", borderRadius: 8, marginBottom: 4, height: 80, objectFit: "cover", opacity: selectedImage && selectedImage.src === src ? 0.7 : 1, transition: 'opacity 0.2s' }} />
              {selectedImage && selectedImage.src === src && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: '#a259ff',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px #a259ff44',
                    zIndex: 2,
                  }}
                >
                  {/* White checkmark SVG */}
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="10" fill="#a259ff" />
                    <path d="M6 11L9 14L14 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </div>
          ))}
          {selectedTab === "Our Collection" && filteredItems.map((item) => {
            const dimensions = imageDimensions[item.src] || { width: 100, height: 100 };
            const aspectRatio = dimensions.width / dimensions.height;
            const containerWidth = 100; // Reduced width for 4 columns
            const containerHeight = containerWidth / aspectRatio;

            return (
              <div
                key={item.src}
                onClick={() => setSelectedImage(selectedImage && selectedImage.src === item.src ? null : { src: item.src, label: item.label })}
                style={{
                  border: selectedImage && selectedImage.src === item.src ? "3px solid #a259ff" : "1px solid #ccc",
                  borderRadius: 12,
                  padding: 0,
                  margin: 0,
                  background: '#bdbdbd',
                  cursor: "pointer",
                  position: "relative",
                  textAlign: "center",
                  boxShadow: selectedImage && selectedImage.src === item.src ? "0 2px 8px #a259ff22" : "none",
                  overflow: 'hidden',
                  width: containerWidth,
                  height: containerHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={item.src}
                  alt={item.label}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: selectedImage && selectedImage.src === item.src ? 0.7 : 1,
                    transition: 'opacity 0.2s',
                    margin: 0,
                    padding: 0
                  }}
                />
                {selectedImage && selectedImage.src === item.src && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      background: '#a259ff',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px #a259ff44',
                      zIndex: 2,
                    }}
                  >
                    {/* White checkmark SVG */}
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="10" cy="10" r="10" fill="#a259ff" />
                      <path d="M6 11L9 14L14 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {selectedImage && (
        <div style={{ marginTop: 16, textAlign: "center", flexShrink: 0 }}>
          <button
            onClick={() => {
              onSelectReferenceImage({
                src: selectedImage.src,
                name: selectedImage.label || "Selected Reference Image"
              });
              onClose();
            }}
            style={{
              background: "#a259ff",
              color: "#fff",
              border: "none",
              borderRadius: 12,
              padding: "12px 32px",
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
              outline: "none",
              transition: "background 0.2s"
            }}
            onMouseOver={(e) => e.target.style.background = "#8a4fd8"}
            onMouseOut={(e) => e.target.style.background = "#a259ff"}
          >
            Use Image
          </button>
        </div>
      )}
    </div>
  );
}

export default PromptImageSelector; 